import * as React from 'react'
import './index.scss'
import { Grid } from '../../../components'
import { Table, Divider, Button, Select, Input } from 'antd'
import Form, { FormComponentProps } from 'antd/lib/form'
import { handleError } from '../../../utilities'
import { API, graphqlOperation } from 'aws-amplify'
import { GraphQLResult } from '@aws-amplify/api/lib/types'
import { deleteUserUploadedFiles } from '../../../graphql/mutations'
import moment from 'moment'
import { PAGE_SIZE, DATE_FORMAT_TABLE } from '../../../constants'
import { File } from './UploadPageWithData'
import bind from 'bind-decorator'
import _ from 'lodash'
import { ColumnProps } from 'antd/lib/table'
import { renderAllData } from '../../../utilities/graphql'

const { Option } = Select

type User = {
    username: string
    name: string
    email: string
    groups: string[]
}

type FilesTable = {
    id: string
    name: string
    email: string
    fileName: string
    url: string
    uploadedAt: string
}

type Props = FormComponentProps

type State = {
    error?: Error
    data: FilesTable[]
    filteredData: FilesTable[]
    loading: boolean
    filtered: boolean
}

class UploadFilesAdmin extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            data: [],
            filteredData: [],
            loading: true,
            filtered: false,
        }
    }

    @bind
    async getUser(username: string) {
        this.setState({ loading: true })

        try {
            const response = await API.get('clickfrenzyFeUsers', `/users/${username}`, {})
            return response.user
        } catch (e) {
            handleError(e)
        } finally {
            this.setState({ loading: false })
        }
    }

    handleDelete = (id: string) => (e: React.MouseEvent) => {
        e.preventDefault()
        this.setState({ loading: true })

        API.graphql(
            graphqlOperation(deleteUserUploadedFiles, {
                input:{ id },
            })
        ).then((response: GraphQLResult) => {
            if (response.data && response.data !== null) {
                const data = this.state.data.filter((file) => {
                    return file.id !== id
                })
                this.setState({ data, loading: false })
            } else {
                handleError(response.errors)
            }
        })
    }

    @bind
    handleColumnChange(value: string) {
        this.props.form.setFieldsValue({ column: value })
    }

    @bind
    handleTextChange(e: any) {
        this.props.form.setFieldsValue({ text: e.target.value })
    }

    @bind
    onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        const { data } = this.state
        this.setState({ filtered: true })

        const column = this.props.form.getFieldValue('column')
        const text = this.props.form.getFieldValue('text')

        const filteredData = _.filter(data, (file) => {
            if (column === 'all') {
                const isFound = _.pullAll(Object.keys(file), ['uploadedAt'])
                .reduce(
                    (acc, key) => {
                        const value = (file as any)[key]
                        if (value.toLowerCase().indexOf(text.toLowerCase()) !== -1) {
                            return true
                        }
                        return acc
                    },
                    false
                )
                return isFound
            }

            return (file as any)[column].toLowerCase().indexOf(text.toLowerCase()) !== -1
        })

        this.setState({ filteredData })
    }

    @bind
    onReset() {
        this.props.form.resetFields()
        this.setState({ filtered: false })
    }

    async componentDidMount() {
        const data: FilesTable[] = []

        const allFiles: File[] = await renderAllData([], undefined, 'listUserUploadedFiless')

        if (allFiles.length > 0) {
            await Promise.all(allFiles.map(async (file: File) => {
                const user = await this.getUser(file.userId)
                data.push({
                    id: file.id,
                    name: user.name,
                    email: user.email,
                    fileName: file.name,
                    url: file.url,
                    uploadedAt: moment(file.uploadedAt).format(DATE_FORMAT_TABLE),
                })
            }))
        }

        this.setState({ data, loading: false })
    }

    render() {
        const { form } = this.props
        const { data, loading, filtered, filteredData } = this.state

        const columns = [
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                filters: _.uniqBy(_.map(data, (item: FilesTable) => {
                    return {
                        text: item.name !== ' ' ? item.name : '-',
                        value: item.name,
                    }
                }),               'value'),
                filterMultiple: true,
                onFilter: (value: string, record: FilesTable) => record.name.indexOf(value) === 0,
                sorter: (a: any, b: any) => +(a.name > b.name) - +(a.name < b.name),
            },
            {
                title: 'Email',
                dataIndex: 'email',
                key: 'email',
                filters: _.uniqBy(_.map(data, (item: FilesTable) => {
                    return {
                        text: item.email !== ' ' ? item.email : '-',
                        value: item.email,
                    }
                }),               'value'),
                filterMultiple: true,
                onFilter: (value: string, record: FilesTable) => record.email.indexOf(value) === 0,
                sorter: (a: any, b: any) => +(a.email > b.email) - +(a.email < b.email),
            },
            {
                title: 'File Name',
                key: 'fileName',
                sorter: (a: any, b: any) => +(a.fileName > b.fileName) - +(a.fileName < b.fileName),
                render: (record: FilesTable) => <a href={record.url} target='_blank'>{record.fileName}</a>,
            },
            {
                title: 'Uploaded Time',
                dataIndex: 'uploadedAt',
                key: 'uploadedAt',
                sorter: (a: any, b: any) => +(a.uploadedAt > b.uploadedAt) - +(a.uploadedAt < b.uploadedAt),
            },
            {
                title: '',
                key: 'action',
                render: (record: FilesTable) => (
                    <span>
                        <a href={record.url} download={true} target='_blank'>View</a>
                        <Divider type='vertical' />
                        <a onClick={this.handleDelete(record.id)}>Delete</a>
                    </span>
                ),
            },
        ]

        return (
            <Grid className='cf-file-upload'>
                <h1>All Files</h1>
                <div className='cf-file-upload__search'>
                    <Form layout='inline' onSubmit={this.onSubmit}>
                        <Form.Item label='Search: '>
                            {form.getFieldDecorator('column', {
                                initialValue: 'all',
                            })(
                                <Select
                                    allowClear={true}
                                    size='large'
                                    style={{ minWidth: '160px' }}
                                    onChange={this.handleColumnChange}
                                    id='column'
                                >
                                    <Option value='all'>All</Option>
                                    {columns.filter((column: ColumnProps<any>) =>
                                        column.dataIndex && !['uploadedAt'].includes(column.dataIndex)
                                    ).map((column, index) => {
                                        return <Option key={index} value={column.dataIndex}>{column.title}</Option>
                                    })}
                                </Select>
                            )}
                        </Form.Item>
                        <Form.Item>
                            {form.getFieldDecorator('text', {
                            })(
                                <Input
                                    type='text'
                                    size='large'
                                    placeholder='Search text'
                                    onChange={this.handleTextChange}
                                />
                            )}
                        </Form.Item>
                        <Form.Item>
                            <Button type='primary' icon='search' htmlType='submit'>
                                Search
                            </Button>
                        </Form.Item>
                        <Form.Item>
                            <Button onClick={this.onReset}>
                                Clear
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
                <div className='cf-file-upload__fileslist'>
                    <Table
                        columns={columns}
                        rowKey='id'
                        dataSource={filtered ? filteredData : data}
                        loading={loading}
                        pagination={filtered ?
                            (filteredData && filteredData.length < PAGE_SIZE ? false : {
                                defaultPageSize: PAGE_SIZE,
                            }) : (data && data.length < PAGE_SIZE ? false : {
                                defaultPageSize: PAGE_SIZE,
                            })
                        }
                    />
                </div>
            </Grid>
        )
    }
}

export default Form.create<Props>()(UploadFilesAdmin)
